import { BarChart, BarItemIdentifier } from "@mui/x-charts";
import {
  DashboardData,
  DateRange,
  Filter,
  SortDirection,
  TicketStatusEnum,
} from "../../../services/swagger";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEnums from "../../../hooks/useEnums";
import { useNavigate } from "react-router";
import { createSearchParams } from "react-router-dom";
import { objectToParamString } from "../../../helpers/params";
import { getISODateString } from "../../../helpers/date";

interface DashboardStatusOverviewProps {
  data: DashboardData;
  dateRange: DateRange;
  rangeDisplacement: number;
}

export default function DashboardStatusOverview(
  props: DashboardStatusOverviewProps
) {
  const { t } = useTranslation("Dashboard");
  const { t: enumsT } = useTranslation("Enums");
  const { statusObj } = useEnums();
  const { data, dateRange, rangeDisplacement } = props;
  const statusOverview = data.statusOverview;
  const theme = useTheme();
  const navigate = useNavigate();

  const empty = useMemo(() => {
    return !statusOverview.some((item) => item.count > 0);
  }, [statusOverview]);

  const handleItemClick = (bar: BarItemIdentifier) => {
    const status = statusOverview[bar.dataIndex];
    const filters: Filter[] = [
      { field: "status", value: status.label, operator: "eq", type: "string" },
    ];
    const startDate = new Date();
    const endDate = new Date();
    startDate.setHours(0);
    startDate.setMinutes(0);
    startDate.setSeconds(0);
    startDate.setMilliseconds(0);
    endDate.setHours(0);
    endDate.setMinutes(0);
    endDate.setSeconds(0);
    endDate.setMilliseconds(0);
    if (dateRange === DateRange.Week) {
      startDate.setDate(
        startDate.getDate() - startDate.getDay() - 7 * rangeDisplacement
      );
      endDate.setDate(
        endDate.getDate() - endDate.getDay() - 7 * (rangeDisplacement - 1)
      );
    } else if (dateRange === DateRange.Month) {
      startDate.setDate(1);
      startDate.setMonth(startDate.getMonth() - rangeDisplacement);
      endDate.setDate(1);
      endDate.setMonth(endDate.getMonth() - rangeDisplacement + 1);
    } else if (dateRange === DateRange.Year) {
      startDate.setDate(1);
      startDate.setMonth(0);
      startDate.setFullYear(startDate.getFullYear() - rangeDisplacement);
      endDate.setDate(1);
      endDate.setMonth(0);
      endDate.setFullYear(endDate.getFullYear() - rangeDisplacement + 1);
    }

    if (dateRange !== DateRange.AllTime) {
      filters.push(
        {
          field: "date",
          value: getISODateString(startDate),
          operator: "gte",
          type: "date",
        },
        {
          field: "date",
          value: getISODateString(endDate),
          operator: "lte",
          type: "date",
        }
      );
    }
    console.log(filters);

    const constructedSearchParams = createSearchParams({
      filters: objectToParamString(filters),
      sortDirection: SortDirection.Desc,
      sortField: "id",
    }).toString();
    navigate({ pathname: "/m/ticket", search: constructedSearchParams });
  };

  return (
    <Paper sx={{ bgcolor: "background.default" }}>
      <Typography
        color="primary"
        variant="h3"
        fontSize={20}
        fontWeight="bold"
        pb={1}
        pt={4}
        pl={4}
      >
        {t("TicketStatus")}
      </Typography>
      <Box width={1} mb={2} pl={empty ? undefined : 4} position="relative">
        {empty ? (
          <Box>
            <BarChart
              series={[{ data: [] }]}
              yAxis={[{ tickMinStep: 1, disableTicks: true }]}
              xAxis={[
                {
                  data: [],
                  scaleType: "band",
                  disableTicks: true,
                },
              ]}
              borderRadius={4}
              colors={[theme.palette.primary.main]}
              height={300}
            />
          </Box>
        ) : (
          <>
            <Box position="absolute" top={67} bgcolor="red">
              {statusOverview.map((status, i) => {
                return (
                  <Box
                    position="absolute"
                    top={i * 50}
                    key={status.label}
                    display="flex"
                    alignItems="center"
                  >
                    <Typography
                      width={90}
                      textOverflow="ellipsis"
                      align="right"
                      fontSize={10}
                      color="text.secondary"
                      noWrap
                    >
                      {enumsT(
                        `${statusObj[status.label as TicketStatusEnum]}` as any
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Box pl={6}>
              <BarChart
                layout="horizontal"
                series={[
                  {
                    data: statusOverview.map((status) => status.count),
                  },
                ]}
                barLabel="value"
                onItemClick={(e, bar) => handleItemClick(bar)}
                yAxis={[
                  {
                    data: statusOverview.map(
                      (status) => statusObj[status.label as TicketStatusEnum]
                    ),
                    position: "right",
                    scaleType: "band",
                    tickLabelStyle: {
                      opacity: 0,
                    },
                    colorMap: {
                      type: "ordinal",
                      colors: [theme.palette.primary.main],
                    },
                    disableTicks: true,
                  },
                ]}
                borderRadius={8}
                xAxis={[{ tickMinStep: 1, disableTicks: true }]}
                height={300}
              />
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
}
